import {
  useGetAllConversationGroups,
  useGetAllConversations,
} from "@/api/queries/conversation";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import {
  useCurrentConversationGroupId,
  useCurrentConversationId,
} from "@/hooks/conversation";

export interface HistoryItem {
  /** Row identity: the group under `keep-conversations-open`, the conversation before it. */
  id: string;
  /** The conversation a press opens. */
  conversationId: string;
  title: string | null;
  hasUnread: boolean;
  /** Whether the open conversation lives in this row. */
  isSelected: boolean;
  /** Whether the row led to a clinic visit, which the stethoscope icon marks. */
  hasVisit: boolean;
}

/**
 * The sidebar's history rows.
 *
 * Under `keep-conversations-open` a chat stays open across visits and follow-ups, so what used
 * to be one conversation per row is now a group of them: the row is the group, and it opens the
 * group's newest Nori conversation. With the flag off this is the conversation list as before.
 * Only the enabled query runs.
 */
export const useHistoryItems = () => {
  const { value: keepConversationsOpen, loading: flagLoading } = useFeatureFlag(
    "keep-conversations-open",
    false,
  );
  const useGroups = Boolean(keepConversationsOpen);

  const conversations = useGetAllConversations({
    enabled: !flagLoading && !useGroups,
  });
  const groups = useGetAllConversationGroups({
    enabled: !flagLoading && useGroups,
  });

  const conversationId = useCurrentConversationId();
  // Only group rows resolve selection through the open conversation, so the conversation list
  // does not pay for the messages query this reads.
  const openConversationGroupId = useCurrentConversationGroupId({
    enabled: useGroups,
  });

  const groupItems: HistoryItem[] = (
    groups.data?.conversationGroups ?? []
  ).flatMap((group) =>
    // A group with no conversation to open is not rendered; the backend already skips
    // those, so this only guards a page cached from an older response.
    group.latest_nori_conversation_id
      ? [
          {
            id: group.id,
            conversationId: group.latest_nori_conversation_id,
            title: group.title,
            hasUnread: group.has_unread,
            // Pressing a row opens this very conversation, so matching the url against it
            // highlights immediately. Being in a visit or an older conversation in the group
            // is the case that has to wait on the open conversation to resolve its group.
            isSelected:
              group.latest_nori_conversation_id === conversationId ||
              Boolean(
                openConversationGroupId && group.id === openConversationGroupId,
              ),
            hasVisit: group.has_visit,
          },
        ]
      : [],
  );

  const conversationItems: HistoryItem[] = (
    conversations.data?.conversations ?? []
  ).map((conversation) => ({
    id: conversation.id,
    conversationId: conversation.id,
    title: conversation.title ?? null,
    hasUnread: Boolean(conversation.has_unread),
    isSelected: Boolean(
      conversationId &&
        (conversation.id === conversationId ||
          conversation.curai_conversation_id === conversationId),
    ),
    hasVisit: Boolean(conversation.curai_conversation_id),
  }));

  // Disabling the query only stops this hook from fetching — the conversation list is kept
  // warm by the rest of the sidebar, so its rows would still render here. Hold both back
  // until the flag says which shape the list is.
  let items: HistoryItem[] = [];
  if (!flagLoading) items = useGroups ? groupItems : conversationItems;

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = useGroups
    ? groups
    : conversations;

  return { items, fetchNextPage, hasNextPage, isFetchingNextPage };
};
